import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { errorCodeOf } from "../components/ErrorAlert";
import { clearBasicFlowShown } from "../guide/BasicFlowModal";

import type { ApiErrorCode, CanvaschoolApi } from "../api/client";
import type {
  AuthSessionEnvelope,
  School,
  StudentAccount,
} from "@canvaschool/contracts";
import type { ReactNode } from "react";

/** The server rotates a session once fewer than 30 minutes remain. */
const REFRESH_BEFORE_EXPIRY_MILLISECONDS = 25 * 60 * 1_000;
/** Retry when a refresh left the expiry unchanged (for example clock skew). */
const REFRESH_RETRY_MILLISECONDS = 5 * 60 * 1_000;
const MAX_TIMER_DELAY_MILLISECONDS = 2_147_483_647;

/** Codes that describe the account rather than a transient failure. */
const ACCOUNT_STATES = new Set<ApiErrorCode>([
  "ACCOUNT_DISABLED",
  "CLASS_ARCHIVED",
  "FORBIDDEN",
]);

export type AnonymousNotice = "PASSWORD_CHANGED";

export type AuthState =
  | { status: "loading" }
  | {
      status: "anonymous";
      error: ApiErrorCode | null;
      notice?: AnonymousNotice;
    }
  /** The session could not be checked; the user is not assumed logged out. */
  | { status: "unavailable"; error: ApiErrorCode }
  | { status: "authenticated"; session: AuthSessionEnvelope };

type AuthContextValue = {
  state: AuthState;
  api: CanvaschoolApi;
  login(loginId: string, password: string): Promise<void>;
  /** Signs out; inside a teacher's account it returns to the administrator. */
  logout(): Promise<void>;
  /** An administrator enters a teacher's account from the teacher list. */
  impersonate(teacherId: string): Promise<void>;
  /** Leaves the teacher's account for the administrator's own session. */
  endImpersonation(): Promise<void>;
  /** A background reload keeps the current state on transient failures. */
  reloadSession(options?: { background?: boolean }): Promise<void>;
  /** The server revokes every session when a password changes. */
  passwordChanged(): void;
  updateStudentProfile(account: StudentAccount): void;
  updateTeacherProfile(school: School): void;
  /** Maps a failed call to its code, rechecking the session when it expired. */
  handleError(error: unknown): ApiErrorCode;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return value;
};

export const AuthProvider = ({
  api,
  children,
}: {
  api: CanvaschoolApi;
  children: ReactNode;
}) => {
  const [state, setState] = useState<AuthState>({ status: "loading" });
  const [refreshRound, setRefreshRound] = useState(0);
  const lastRefreshedExpiry = useRef<string | null>(null);

  const reloadSession = useCallback(
    async (options: { background?: boolean } = {}) => {
      try {
        const session = await api.getSession();
        setState((current) =>
          session
            ? { status: "authenticated", session }
            : {
                status: "anonymous",
                error: null,
                notice:
                  current.status === "anonymous" ? current.notice : undefined,
              },
        );
      } catch (error) {
        const code = errorCodeOf(error);
        if (ACCOUNT_STATES.has(code)) {
          setState({ status: "anonymous", error: code });
        } else if (!options.background) {
          setState({ status: "unavailable", error: code });
        }
        // A background check keeps the signed-in page; the refresh timer
        // tries again later.
      }
    },
    [api],
  );

  useEffect(() => {
    void reloadSession();
  }, [reloadSession]);

  const expiresAt =
    state.status === "authenticated" ? state.session.expiresAt : null;
  useEffect(() => {
    if (!expiresAt) {
      return;
    }
    const delay =
      lastRefreshedExpiry.current === expiresAt
        ? REFRESH_RETRY_MILLISECONDS
        : new Date(expiresAt).getTime() -
          Date.now() -
          REFRESH_BEFORE_EXPIRY_MILLISECONDS;
    const timer = window.setTimeout(async () => {
      lastRefreshedExpiry.current = expiresAt;
      try {
        await api.refreshSession();
      } catch {
        // Another tab may already have rotated the cookie; resync decides.
      }
      // Adopt the session the cookie now holds: new ID, CSRF token, expiry.
      await reloadSession({ background: true });
      setRefreshRound((round) => round + 1);
    }, Math.min(MAX_TIMER_DELAY_MILLISECONDS, Math.max(0, delay)));
    return () => window.clearTimeout(timer);
  }, [api, expiresAt, refreshRound, reloadSession]);

  const value = useMemo<AuthContextValue>(() => {
    const endImpersonation = async () => {
      try {
        await api.endImpersonation();
      } finally {
        // The cookie now holds the administrator's session, or none.
        await reloadSession();
      }
    };
    return {
      state,
      api,
      async login(loginId, password) {
        const session = await api.login(loginId, password);
        // A fresh login opens the basic flow again, even in the same tab.
        clearBasicFlowShown(session.principal.userId);
        setState({ status: "authenticated", session });
      },
      async logout() {
        if (state.status === "authenticated" && state.session.impersonator) {
          await endImpersonation();
          return;
        }
        try {
          await api.logout();
        } finally {
          setState({ status: "anonymous", error: null });
        }
      },
      async impersonate(teacherId) {
        const session = await api.impersonateTeacher(teacherId);
        setState({ status: "authenticated", session });
      },
      endImpersonation,
      reloadSession,
      passwordChanged() {
        setState({
          status: "anonymous",
          error: null,
          notice: "PASSWORD_CHANGED",
        });
      },
      updateStudentProfile(account) {
        setState((current) =>
          current.status === "authenticated"
            ? {
                status: "authenticated",
                session: {
                  ...current.session,
                  studentProfile: {
                    userId: account.userId,
                    classId: account.classId,
                    studentNumber: account.studentNumber,
                    loginId: account.loginId,
                    displayName: account.displayName,
                    enabled: account.enabled,
                    lastLoginAt: account.lastLoginAt,
                  },
                },
              }
            : current,
        );
      },
      updateTeacherProfile(school) {
        setState((current) =>
          current.status === "authenticated"
            ? {
                status: "authenticated",
                session: { ...current.session, teacherProfile: { school } },
              }
            : current,
        );
      },
      handleError(error) {
        const code = errorCodeOf(error);
        if (code === "UNAUTHENTICATED") {
          // A rotated cookie is not a logout; only a missing session is.
          void reloadSession({ background: true });
        }
        return code;
      },
    };
  }, [api, reloadSession, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
